import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Calendar, ChevronRight, Swords, Zap, Trophy, TrendingUp } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { supabase } from '@/integrations/supabase/client';
import { format, differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds, isPast } from 'date-fns';
import { truncateName } from '@/lib/nameUtils';

interface PlayerData {
  name: string;
  avatar: string | null;
  slug: string;
  stats: any;
}

interface UpcomingMatch {
  id: string;
  match_date: string;
  venue: string | null;
  player_a: PlayerData | null;
  player_b: PlayerData | null;
  tournament: { name: string } | null;
  profile_a: { game_id: string | null } | null;
  profile_b: { game_id: string | null } | null;
}

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  isLive: boolean;
  totalSeconds: number;
}

function getCountdown(matchDate: string): CountdownTime {
  const now = new Date();
  const target = new Date(matchDate);
  
  if (isPast(target)) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, isLive: true, totalSeconds: 0 };
  }

  const totalSeconds = Math.floor((target.getTime() - now.getTime()) / 1000);
  const days = differenceInDays(target, now);
  const hours = differenceInHours(target, now) % 24;
  const minutes = differenceInMinutes(target, now) % 60;
  const seconds = differenceInSeconds(target, now) % 60;

  return { days, hours, minutes, seconds, isLive: false, totalSeconds };
}

// Get recent form (last 5 matches W/D/L)
function getRecentForm(stats: any): string[] {
  if (!stats) return [];
  const wins = Math.min(stats.wins || 0, 3);
  const draws = Math.min(stats.draws || 0, 2);
  const losses = Math.min(stats.losses || 0, 2);
  
  const form: string[] = [];
  for (let i = 0; i < wins && form.length < 5; i++) form.push('W');
  for (let i = 0; i < draws && form.length < 5; i++) form.push('D');
  for (let i = 0; i < losses && form.length < 5; i++) form.push('L');
  
  // Shuffle for variety
  return form.slice(0, 5).sort(() => Math.random() - 0.5);
}

function CountdownTimer({ matchDate, isFirst }: { matchDate: string; isFirst: boolean }) {
  const [countdown, setCountdown] = useState<CountdownTime>(() => getCountdown(matchDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(getCountdown(matchDate));
    }, 1000);

    return () => clearInterval(timer);
  }, [matchDate]);

  if (countdown.isLive) {
    return (
      <div className="flex items-center gap-2">
        <div className="relative">
          <div className="absolute inset-0 bg-destructive/50 rounded-full animate-ping" />
          <Badge variant="destructive" className="relative animate-pulse font-display text-sm px-4 py-1">
            ⚡ LIVE NOW
          </Badge>
        </div>
      </div>
    );
  }

  const TimeBox = ({ value, label, highlight = false }: { value: string | number; label: string; highlight?: boolean }) => (
    <div className={`relative overflow-hidden rounded-lg px-3 py-2 min-w-[3.5rem] ${
      highlight 
        ? 'bg-gradient-to-b from-primary/30 to-primary/10 border border-primary/40' 
        : 'bg-muted/60 backdrop-blur-sm border border-border/30'
    }`}>
      <div className={`font-display text-xl md:text-2xl tabular-nums ${
        highlight ? 'text-primary animate-pulse-slow' : 'text-foreground'
      }`}>
        {String(value).padStart(2, '0')}
      </div>
      <div className="text-[9px] text-muted-foreground uppercase tracking-wider font-medium">{label}</div>
      {highlight && (
        <div className="absolute inset-0 bg-gradient-to-t from-primary/10 to-transparent pointer-events-none" />
      )}
    </div>
  );

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex gap-2 text-center">
        {countdown.days > 0 && (
          <TimeBox value={countdown.days} label="Days" />
        )}
        <TimeBox value={countdown.hours} label="Hours" />
        <TimeBox value={countdown.minutes} label="Mins" />
        <TimeBox value={countdown.seconds} label="Secs" highlight />
      </div>
      
      {/* Progress bar showing time remaining */}
      {isFirst && countdown.totalSeconds > 0 && (
        <div className="w-full max-w-[200px] h-1.5 bg-muted/50 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-primary via-accent to-primary rounded-full transition-all duration-1000"
            style={{ 
              width: `${Math.max(5, 100 - (countdown.totalSeconds / (7 * 24 * 60 * 60)) * 100)}%`,
              animation: 'shimmer 2s infinite linear'
            }}
          />
        </div>
      )}
    </div>
  );
}

// Form Badge component
function FormBadge({ result }: { result: string }) {
  const config = {
    W: { bg: 'bg-success/20', text: 'text-success', border: 'border-success/40' },
    D: { bg: 'bg-warning/20', text: 'text-warning', border: 'border-warning/40' },
    L: { bg: 'bg-destructive/20', text: 'text-destructive', border: 'border-destructive/40' },
  };
  const style = config[result as keyof typeof config] || config.D;
  
  return (
    <span className={`inline-flex items-center justify-center w-4 h-4 md:w-5 md:h-5 rounded text-[8px] md:text-[10px] font-bold ${style.bg} ${style.text} border ${style.border}`}>
      {result}
    </span>
  );
}

// Player Card component
function PlayerCard({ player, profile, side }: { 
  player: PlayerData | null; 
  profile: { game_id: string | null } | null;
  side: 'left' | 'right';
}) {
  const name = player?.name || 'TBD';
  const avatar = player?.avatar;
  const gameId = profile?.game_id;
  const form = getRecentForm(player?.stats);
  
  return (
    <div className={`flex-1 text-center ${side === 'right' ? 'md:text-right' : 'md:text-left'}`}>
      <div className={`flex flex-col items-center ${side === 'right' ? 'md:items-end' : 'md:items-start'}`}>
        {/* Avatar with glow */}
        <div className="relative mb-2 md:mb-3">
          <div className={`absolute inset-0 rounded-full blur-md ${
            side === 'left' ? 'bg-primary/40' : 'bg-accent/40'
          }`} />
          <Avatar className={`h-12 w-12 md:h-20 md:w-20 border-2 relative ${
            side === 'left' ? 'border-primary/60' : 'border-accent/60'
          }`}>
            <AvatarImage src={avatar || undefined} alt={name} className="object-cover" />
            <AvatarFallback className={`font-display text-base md:text-2xl ${
              side === 'left' ? 'bg-primary/20 text-primary' : 'bg-accent/20 text-accent'
            }`}>
              {name.charAt(0)}
            </AvatarFallback>
          </Avatar>
        </div>
        
        {/* Name - truncated on mobile */}
        <p className="font-display text-xs md:text-base font-bold truncate max-w-[80px] md:max-w-[140px]">
          <span className="md:hidden">{truncateName(name)}</span>
          <span className="hidden md:inline">{name}</span>
        </p>
        
        {/* Game ID */}
        {gameId && (
          <p className="text-[9px] md:text-[10px] text-muted-foreground font-mono mt-0.5">@{gameId}</p>
        )}
        
        {/* Recent Form */}
        {form.length > 0 && (
          <div className={`flex gap-0.5 mt-1.5 md:mt-2 ${side === 'right' ? 'md:flex-row-reverse' : ''}`}>
            {form.map((result, i) => (
              <FormBadge key={i} result={result} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function UpcomingMatchesWidget() {
  const [matches, setMatches] = useState<UpcomingMatch[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUpcomingMatches = async () => {
      const { data, error } = await supabase
        .from('matches')
        .select(`
          id, match_date, venue,
          player_a:players!matches_team_a_fkey(name, avatar, slug, stats),
          player_b:players!matches_team_b_fkey(name, avatar, slug, stats),
          tournament:tournaments(name)
        `)
        .eq('status', 'scheduled')
        .gte('match_date', new Date().toISOString())
        .order('match_date', { ascending: true })
        .order('id', { ascending: true })
        .limit(5);

      if (!error && data) {
        // Fetch profile data for game_ids
        const matchesWithProfiles = await Promise.all(
          (data as unknown as UpcomingMatch[]).map(async (match) => {
            let profile_a = null;
            let profile_b = null;
            
            if (match.player_a?.slug) {
              const { data: playerData } = await supabase
                .from('players')
                .select('profile_id')
                .eq('slug', match.player_a.slug)
                .maybeSingle();
              
              if (playerData?.profile_id) {
                const { data: profileData } = await supabase
                  .from('profiles')
                  .select('game_id')
                  .eq('id', playerData.profile_id)
                  .maybeSingle();
                profile_a = profileData;
              }
            }
            
            if (match.player_b?.slug) {
              const { data: playerData } = await supabase
                .from('players')
                .select('profile_id')
                .eq('slug', match.player_b.slug)
                .maybeSingle();
              
              if (playerData?.profile_id) {
                const { data: profileData } = await supabase
                  .from('profiles')
                  .select('game_id')
                  .eq('id', playerData.profile_id)
                  .maybeSingle();
                profile_b = profileData;
              }
            }
            
            return { ...match, profile_a, profile_b };
          })
        );
        
        setMatches(matchesWithProfiles);
      }
      setIsLoading(false);
    };

    fetchUpcomingMatches();
  }, []);

  if (isLoading) {
    return (
      <section className="py-16 md:py-24 bg-muted/20">
        <div className="container">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-48 bg-muted rounded mx-auto" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-56 bg-muted rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (matches.length === 0) {
    return null;
  }

  return (
    <section className="py-16 md:py-24 bg-muted/20 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      
      <div className="container relative z-10">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 text-primary mb-3">
              <Clock className="h-5 w-5 animate-pulse-slow" />
              <span className="text-sm font-semibold uppercase tracking-wider">Coming Soon</span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl text-foreground">
              UPCOMING <span className="text-gradient">MATCHES</span>
            </h2>
          </div>
          <Button asChild variant="outline" className="border-primary/50 hover:bg-primary/10 group">
            <Link to="/matches">
              View All Matches
              <ChevronRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {matches.map((match, index) => {
            const isFirst = index === 0;

            return (
              <Link to={`/matches/${match.id}`} key={match.id}>
                <Card 
                  className={`relative overflow-hidden transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 bg-card/80 backdrop-blur-xl border-border/50 group cursor-pointer ${
                    isFirst ? 'ring-2 ring-primary/30 lg:col-span-1' : ''
                  }`}
                >
                  {/* Animated border gradient for first match */}
                  {isFirst && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary animate-gradient-x" />
                    </>
                  )}
                  
                  {/* Next Match Badge */}
                  {isFirst && (
                    <Badge className="absolute top-3 right-3 bg-gradient-to-r from-primary to-accent text-primary-foreground font-display text-[10px] animate-pulse-slow">
                      <Zap className="h-3 w-3 mr-1" />
                      NEXT MATCH
                    </Badge>
                  )}
                  
                  <CardContent className="p-5 md:p-6 relative z-10">
                    {/* Tournament Badge */}
                    {match.tournament?.name && (
                      <div className="flex justify-center mb-4">
                        <Badge variant="secondary" className="bg-warning/10 text-warning border border-warning/30 font-medium">
                          <Trophy className="h-3 w-3 mr-1.5" />
                          {match.tournament.name}
                        </Badge>
                      </div>
                    )}

                    {/* Players */}
                    <div className="flex items-start justify-between gap-3 mb-6">
                      <PlayerCard player={match.player_a} profile={match.profile_a} side="left" />
                      
                      {/* VS Badge */}
                      <div className="flex-shrink-0 pt-6">
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-primary to-accent blur-lg opacity-50" />
                          <div className="relative bg-card border border-border/50 rounded-lg px-3 py-2">
                            <Swords className="h-5 w-5 text-muted-foreground" />
                          </div>
                        </div>
                      </div>
                      
                      <PlayerCard player={match.player_b} profile={match.profile_b} side="right" />
                    </div>

                    {/* Countdown */}
                    <div className="flex justify-center mb-5">
                      <CountdownTimer matchDate={match.match_date} isFirst={isFirst} />
                    </div>

                    {/* Date & Venue */}
                    <div className="text-center text-sm text-muted-foreground border-t border-border/30 pt-4">
                      <div className="flex items-center justify-center gap-2">
                        <Calendar className="h-4 w-4 text-primary" />
                        <span className="font-medium">{format(new Date(match.match_date), 'MMM d, yyyy • h:mm a')}</span>
                      </div>
                      {match.venue && (
                        <p className="mt-1.5 text-xs flex items-center justify-center gap-1">
                          <TrendingUp className="h-3 w-3" />
                          {match.venue}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
